import React, { useMemo, useState, useEffect } from "react";
import MemoryMarkdown from "../AgentMemory/MemoryMarkdown";
import { coerceStructuredValue, ToolPayloadPanel } from "./ToolCallDetails";

/**
 * Heuristic: treat text as markdown when it has clear structural markers.
 * Plain short prose stays in raw/plain mode by default.
 */
export function looksLikeMarkdown(text) {
  if (!text || typeof text !== "string") return false;
  const sample = text.length > 4000 ? text.slice(0, 4000) : text;
  // Prefer JSON detection first — compact JSON is not markdown.
  if (looksLikeJson(sample)) return false;
  if (/(?:^|\n)\s{0,3}#{1,6}\s+\S/.test(sample)) return true;
  if (/```/.test(sample)) return true;
  if (/(?:^|\n)\s{0,3}>\s+\S/.test(sample)) return true;
  if (/\[[^\]]+\]\([^)]+\)/.test(sample)) return true;
  if (/\*\*[^*\n]{1,200}\*\*/.test(sample)) return true;
  const listHits = sample.match(/(?:^|\n)\s{0,3}(?:[-*+]|\d+\.)\s+\S/g);
  if (listHits && listHits.length >= 2) return true;
  if ((sample.match(/`[^`\n]+`/g) || []).length >= 2) return true;
  return false;
}

export function looksLikeJson(value) {
  if (value == null) return false;
  if (typeof value === "object") return true;
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (
    !(
      (trimmed.startsWith("{") && (trimmed.endsWith("}") || /[…\u2026]/.test(trimmed))) ||
      (trimmed.startsWith("[") && (trimmed.endsWith("]") || /[…\u2026]/.test(trimmed)))
    )
  ) {
    return false;
  }
  const coerced = coerceStructuredValue(trimmed);
  return coerced.kind === "json";
}

/**
 * Rich message / task body: JSON → field cards, markdown → rendered prose,
 * otherwise plain text. Always allows a Raw view when structured.
 */
/**
 * Prose block (assistant text, prompt, operator reply). Markdown-looking text
 * renders as markdown by default — same default as the Clean view — with a
 * small Markdown/Raw toggle; plain prose stays raw and gets no toggle chrome.
 */
export function ProseText({ text, className }) {
  const isMarkdown = useMemo(() => looksLikeMarkdown(text), [text]);
  const [raw, setRaw] = useState(false);

  if (!isMarkdown) {
    return <p className={className}>{text}</p>;
  }

  return (
    <div>
      <div className='flex justify-end mb-0.5'>
        <div className='inline-flex rounded border border-gray-200 bg-white p-px'>
          <button
            type='button'
            onClick={() => setRaw(false)}
            className={`rounded px-1.5 py-px text-[9px] font-semibold ${
              !raw
                ? "bg-emerald-50 text-emerald-800"
                : "text-gray-400 hover:text-gray-600"
            }`}>
            Markdown
          </button>
          <button
            type='button'
            onClick={() => setRaw(true)}
            className={`rounded px-1.5 py-px text-[9px] font-semibold ${
              raw
                ? "bg-emerald-50 text-emerald-800"
                : "text-gray-400 hover:text-gray-600"
            }`}>
            Raw
          </button>
        </div>
      </div>
      {raw ? (
        <p className={className}>{text}</p>
      ) : (
        <MemoryMarkdown text={text} />
      )}
    </div>
  );
}


export default function TimelineMarkdown({
  text,
  emptyLabel = "(empty)",
  className = "",
  maxHeightClass = "max-h-[28rem]",
  label = null,
}) {
  const value = text;
  const isJson = useMemo(() => looksLikeJson(value), [value]);
  const stringValue = value == null ? "" : typeof value === "string" ? value : "";
  const isMarkdown = useMemo(
    () => !isJson && looksLikeMarkdown(stringValue),
    [isJson, stringValue]
  );
  const [modeOverride, setModeOverride] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const valueKey =
    value == null
      ? ""
      : typeof value === "string"
        ? value
        : (() => {
            try {
              return JSON.stringify(value);
            } catch {
              return String(value);
            }
          })();

  useEffect(() => {
    setModeOverride(null);
    setExpanded(false);
  }, [valueKey]);

  const defaultMode = isJson || isMarkdown ? "rendered" : "raw";
  const mode = modeOverride ?? defaultMode;
  const showToggle = (isJson || isMarkdown) && (isJson || stringValue.trim().length > 0);
  const plainLength =
    typeof value === "string"
      ? value.length
      : valueKey.length;
  const isLong = plainLength > 600;

  if (value == null || (typeof value === "string" && !value.trim())) {
    return <div className={`text-sm text-gray-400 italic ${className}`}>{emptyLabel}</div>;
  }

  // Structured JSON → dedicated field-tree panel (has its own Fields/JSON toggle).
  if (isJson) {
    return (
      <div className={`min-w-0 ${className}`}>
        <ToolPayloadPanel
          label={label || "Payload"}
          value={value}
          showHeader={Boolean(label)}
        />
      </div>
    );
  }

  return (
    <div className={`min-w-0 ${className}`}>
      {showToggle && (
        <ModeToggle mode={mode} onChange={setModeOverride} jsonMode={false} />
      )}

      <div
        className={`rounded-xl border border-gray-100/80 bg-white/70 ${
          expanded ? "" : `${maxHeightClass} overflow-auto`
        }`}
      >
        {mode === "rendered" && isMarkdown ? (
          <div className="px-3 py-2">
            <MemoryMarkdown text={stringValue} />
          </div>
        ) : (
          <pre className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed m-0 px-3 py-2 font-sans">
            {stringValue || valueKey}
          </pre>
        )}
      </div>

      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-900 underline focus:outline-none"
        >
          {expanded ? "Collapse" : `Expand full message (${plainLength.toLocaleString()} chars)`}
        </button>
      )}
    </div>
  );
}

function ModeToggle({ mode, onChange, jsonMode }) {
  const renderedLabel = jsonMode ? "Fields" : "Markdown";
  const rawLabel = jsonMode ? "Raw" : "Raw";
  return (
    <div className="mb-2 flex items-center justify-end gap-2">
      <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
        <button
          type="button"
          onClick={() => onChange("rendered")}
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
            mode === "rendered"
              ? "bg-emerald-50 text-emerald-800"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {renderedLabel}
        </button>
        <button
          type="button"
          onClick={() => onChange("raw")}
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
            mode === "raw"
              ? "bg-emerald-50 text-emerald-800"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {rawLabel}
        </button>
      </div>
    </div>
  );
}
